import React, { useEffect, useState } from 'react';
import {
  Box, Button, Menu, MenuItem, CircularProgress, Typography,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import GridSelector from './GridSelector';
import ProductGrid from './ProductGrid';
import SalesPanel from './SalesPanel';
import AddProductsToCategoryModal from './categories_view/AddProductsToCategoryModal';
import OptionalsModal from './billing_modal/OptionalsModal';
import { getCategoriesByLocation } from '../../../services/CategoryViewService';
import { getProducts, getProductById, removeProductFromCategoryView } from '../../../services/ProductService';
import { permissionOptions } from '../../../utils/Enums';

import SnackbarUtils from '../../../utils/SnackbarUtils';
import { canAddToTicket, MIXED_CURRENCY_MESSAGE } from './ticketCurrency';
const SalesTerminal = ({ locationId, initialStoreCreditCode, onStoreCreditConsumed }) => {
  const userPermissions = JSON.parse(localStorage.getItem('permissions')) || [];
  const hasPermission = (permissionLabel) => {
    const permissionId = permissionOptions.find(p => p.label === permissionLabel)?.id;
    return permissionId ? userPermissions.includes(permissionId) : false;
  };
  // El backend habilita POST y DELETE /products/category_view con cualquiera de estos permisos
  // (ver ProductController.kt requiredPermissions de esas rutas) - mantener en sync.
  const canManageCategoryViewProducts =
    hasPermission('MANAGE_CATEGORY_VIEW_PRODUCTS') ||
    hasPermission('UPDATE_PRODUCTS') ||
    hasPermission('UPDATE_CATEGORY');

  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(true);
  // Usar breadcrumb para soportar N niveles
  const [categoryPath, setCategoryPath] = useState([]);
  const [addProductModalOpen, setAddProductModalOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [optionalsModalProduct, setOptionalsModalProduct] = useState(null);
  // Estado para controlar el modo de eliminación
  const [deleteModeEnabled, setDeleteModeEnabled] = useState(false);
  // IDs de productos que se están eliminando (evita race y da feedback visual)
  const [deletingProductIds, setDeletingProductIds] = useState(new Set());
  // Estado para el menú de acciones
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  // Producto pendiente de confirmar su eliminación de la categoría: { productId, categoryViewId }
  const [pendingRemoval, setPendingRemoval] = useState(null);

  useEffect(() => {
    getCategoriesByLocation(locationId).then(setCategories);
  }, [locationId]);

  useEffect(() => {
    setProductsLoading(true);
    getProducts(locationId, 1, 100000, '')
      .then((data) => {
        if (data && typeof data === 'object') {
          const products = data.products || data.data || [];
          setAllProducts(products);
        } else {
          setAllProducts(data || []);
        }
      })
      .finally(() => setProductsLoading(false));
  }, [locationId]);

  // Sincronizar el breadcrumb cuando se actualicen las categorías
  useEffect(() => {
    if (categoryPath.length === 0 || !categories || categories.length === 0) {
      return;
    }

    // Función recursiva para encontrar una categoría por ID en el árbol
    const findCategoryById = (cats, id) => {
      for (const cat of cats) {
        if (cat.id === id) {
          return cat;
        }
        if (cat.children && cat.children.length > 0) {
          const found = findCategoryById(cat.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    // Actualizar el breadcrumb con las nuevas referencias de categorías
    const updatedPath = categoryPath.map(cat => {
      const updatedCat = findCategoryById(categories, cat.id);
      return updatedCat || cat; // Mantener la referencia antigua si no se encuentra
    }).filter(Boolean); // Eliminar nulls si alguna categoría fue eliminada

    // Solo actualizar si hay cambios reales
    if (updatedPath.length !== categoryPath.length) {
      setCategoryPath(updatedPath);
    } else {
      // Verificar si alguna categoría cambió
      const hasChanges = updatedPath.some((cat, index) => 
        !categoryPath[index] || cat.id !== categoryPath[index].id
      );
      if (hasChanges) {
        setCategoryPath(updatedPath);
      }
    }
  }, [categories, categoryPath.length]); // Incluir categoryPath.length para evitar loops infinitos

  const refreshSalesData = async () => {
    setProductsLoading(true);
    try {
      const fetchedCategories = await getCategoriesByLocation(locationId);
      const fetchedProducts = await getProducts(locationId, 1, 100000, '');
      setCategories(fetchedCategories);
      if (fetchedProducts && typeof fetchedProducts === 'object') {
        setAllProducts(fetchedProducts.products || fetchedProducts.data || []);
      } else {
        setAllProducts(fetchedProducts || []);
      }
    } finally {
      setProductsLoading(false);
    }
  };

  const handleHomeClick = () => {
    setCategoryPath([]);
  };

  // Navegar a una categoría (agregar al breadcrumb)
  const handleCategorySelect = (category) => {
    setCategoryPath(prev => [...prev, category]);
  };

  // Navegar hacia atrás en el breadcrumb
  const handleBackClick = () => {
    setCategoryPath(prev => prev.slice(0, -1));
  };

  const handleAddProduct = () => {
    setAddProductModalOpen(true);
    handleActionsMenuClose();
  };

  const handleActionsMenuOpen = (event) => {
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleToggleDeleteMode = () => {
    setDeleteModeEnabled(prev => !prev);
    handleActionsMenuClose();
  };

  const handleAddToCart = (product, optionalPayload = null) => {
    if (!canAddToTicket(cartItems, product)) {
      SnackbarUtils.warning(MIXED_CURRENCY_MESSAGE);
      return;
    }
    const hasOptionals = optionalPayload?.optionalSelections?.length > 0;
    // Por defecto se cobra el precio del canal Local (con fallback al precio base);
    // el "extra" de opcionales se guarda aparte para poder recalcular al cambiar el origen de la venta.
    const basePrice = product.priceLocal ?? product.price;
    const extrasAmount = optionalPayload?.unitPrice != null ? optionalPayload.unitPrice - product.price : 0;
    const unitPrice = basePrice + extrasAmount;
    const optionalSelections = optionalPayload?.optionalSelections ?? null;

    const updatedItems = (() => {
      if (hasOptionals) {
        return [...cartItems, { ...product, qty: 1, unitPrice, extrasAmount, optionalSelections }];
      }
      const existing = cartItems.find(
        (item) => item.id === product.id && !(item.optionalSelections?.length > 0)
      );
      if (existing) {
        return cartItems.map((item) =>
          item.id === product.id && !(item.optionalSelections?.length > 0)
            ? { ...item, qty: item.qty + 1 }
            : item
        );
      }
      return [...cartItems, { ...product, qty: 1, unitPrice, extrasAmount, optionalSelections }];
    })();

    setCartItems((prev) => {
      const withLoadingFlag = updatedItems.map(item => ({ ...item, _triggerLoading: true }));
      return withLoadingFlag;
    });
  };

  const handleProductClick = (product) => {
    getProductById(product.id, locationId)
      .then((fullProduct) => {
        const p = fullProduct || product;
        const optionals = p.optionals ?? [];
        const maxOpt = p.maxOptionalSelections ?? null;
        if (optionals.length > 0 && (maxOpt == null || maxOpt > 0)) {
          setOptionalsModalProduct(p);
        } else {
          handleAddToCart(p);
        }
      })
      .catch(() => handleAddToCart(product));
  };

  // Se usa un Dialog de MUI (no window.confirm/alert): los diálogos nativos dejan la ventana de
  // Electron sin foco de teclado y los inputs (ej. monto en efectivo) quedan imposibles de seleccionar.
  const handleRemoveProduct = (productId, categoryViewId) => {
    setPendingRemoval({ productId, categoryViewId });
  };

  const handleConfirmRemoveProduct = async () => {
    const { productId, categoryViewId } = pendingRemoval;
    setPendingRemoval(null);
    setDeletingProductIds(prev => new Set(prev).add(productId));
    try {
      await removeProductFromCategoryView(locationId, productId, categoryViewId);
      // Solo actualización optimista: evita que refreshSalesData() pise respuestas si se eliminan varios seguidos
      setAllProducts(prev =>
        Array.isArray(prev) ? prev.map(p =>
          p.id === productId
            ? { ...p, categoryView: p.categoryView?.filter(id => id !== categoryViewId) || [] }
            : p
        ) : []
      );
    } catch (error) {
      console.error('Error al eliminar producto de categoría:', error);
      SnackbarUtils.error('Error al eliminar el producto de la categoría');
    } finally {
      setDeletingProductIds(prev => {
        const next = new Set(prev);
        next.delete(productId);
        return next;
      });
    }
  };

  // Determinar qué categoría está activa (la última del breadcrumb)
  const activeCategory = categoryPath.length > 0 ? categoryPath[categoryPath.length - 1] : null;

  // Obtener las categorías hijas del nivel activo
  const getChildrenCategories = () => {
    if (!activeCategory) {
      return categories; // Nivel raíz
    }
    return activeCategory.children || [];
  };

  // Obtener productos de la categoría activa
  const getProductsForActiveCategory = () => {
    if (!activeCategory || !Array.isArray(allProducts)) {
      return [];
    }
    return allProducts.filter(p => {
      const hasCategoryView = p.categoryView?.includes(activeCategory.id);
      return hasCategoryView;
    });
  };

  const childrenCategories = getChildrenCategories();
  const productsInCategory = getProductsForActiveCategory();
  const hasChildren = childrenCategories && childrenCategories.length > 0;
  const hasProducts = productsInCategory && productsInCategory.length > 0;

  return (
    <Box sx={{ display: 'flex', flexGrow: 1, height: '100%', overflow: 'hidden' }}>
      <Box sx={{ flex: 2, p: 3, overflow: 'auto' }}>
        {/* Nivel raíz - mostrar todas las categorías principales */}
        {categoryPath.length === 0 && (
          <GridSelector items={categories} onSelect={handleCategorySelect} />
        )}

        {/* Nivel con categoría seleccionada - mostrar hijos y productos */}
        {categoryPath.length > 0 && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Button variant="outlined" onClick={handleBackClick}>
                Atrás
              </Button>
              {canManageCategoryViewProducts && (
                <Button
                  variant="outlined"
                  onClick={handleActionsMenuOpen}
                  color={deleteModeEnabled ? 'error' : 'inherit'}
                >
                  Acciones
                </Button>
              )}
            </Box>

            {/* Menú de acciones */}
            <Menu
              anchorEl={actionsMenuAnchor}
              open={Boolean(actionsMenuAnchor)}
              onClose={handleActionsMenuClose}
            >
              {canManageCategoryViewProducts && (
                <MenuItem onClick={handleAddProduct}>
                  Agregar productos
                </MenuItem>
              )}
              {canManageCategoryViewProducts && (
                <MenuItem onClick={handleToggleDeleteMode}>
                  {deleteModeEnabled ? 'Deshabilitar eliminación' : 'Habilitar eliminación'}
                </MenuItem>
              )}
            </Menu>

            {/* Mostrar categorías hijas si existen */}
            {hasChildren && (
              <Box sx={{ mb: 3 }}>
                <GridSelector 
                  items={childrenCategories} 
                  onSelect={handleCategorySelect} 
                />
              </Box>
            )}

            {/* Loading mientras se cargan los productos */}
            {productsLoading && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '300px',
                  width: '100%',
                  mt: hasChildren ? 3 : 0,
                }}
              >
                <CircularProgress size={48} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Cargando productos...
                </Typography>
              </Box>
            )}

            {/* Mostrar productos si existen (y no está cargando) */}
            {!productsLoading && hasProducts && (
              <Box sx={{ mt: hasChildren ? 3 : 0 }}>
                <ProductGrid
                  products={productsInCategory}
                  onProductClick={handleProductClick}
                  onRemoveProduct={deleteModeEnabled ? handleRemoveProduct : null}
                  categoryViewId={activeCategory?.id}
                  locationId={locationId}
                  showDeleteButtons={deleteModeEnabled}
                  deletingProductIds={deletingProductIds}
                />
              </Box>
            )}

            {/* Mensaje si no hay ni hijos ni productos (y ya terminó de cargar) */}
            {!productsLoading && !hasChildren && !hasProducts && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '500px',
                  width: '100%',
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  {canManageCategoryViewProducts && (
                    <Box sx={{ mb: 2 }}>
                      <Button variant="contained" color="primary" onClick={handleAddProduct}>
                        Agregar productos a esta categoría
                      </Button>
                    </Box>
                  )}
                  <Box sx={{ color: 'text.secondary' }}>
                    No hay categorías ni productos en este nivel.
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      <Dialog open={Boolean(pendingRemoval)} onClose={() => setPendingRemoval(null)}>
        <DialogTitle>Eliminar producto</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea eliminar este producto de esta categoría?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRemoval(null)}>Cancelar</Button>
          <Button onClick={handleConfirmRemoveProduct} color="error" variant="contained" autoFocus>
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      <OptionalsModal
        open={Boolean(optionalsModalProduct)}
        product={optionalsModalProduct}
        locationId={locationId}
        onConfirm={(payload) => {
          if (optionalsModalProduct) {
            handleAddToCart(optionalsModalProduct, payload);
            setOptionalsModalProduct(null);
          }
        }}
        onClose={() => setOptionalsModalProduct(null)}
      />

      <AddProductsToCategoryModal
        open={addProductModalOpen}
        allProducts={Array.isArray(allProducts) ? allProducts : []}
        onClose={() => setAddProductModalOpen(false)}
        locationId={locationId}
        subcategoryId={activeCategory?.id}
        onSaved={refreshSalesData}
        onProductAdded={(newIds) => {
          if (activeCategory) {
            setAllProducts(prev =>
              Array.isArray(prev) ? prev.map(p => newIds.includes(p.id)
                ? { ...p, categoryView: [...(p.categoryView ?? []), activeCategory.id] }
                : p
              ) : []
            );
          }
        }}
      />

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <SalesPanel
          locationId={locationId}
          items={cartItems}
          onUpdate={setCartItems}
          initialStoreCreditCode={initialStoreCreditCode}
          onStoreCreditConsumed={onStoreCreditConsumed}
        />
      </Box>
    </Box>
  );
};

export default SalesTerminal;
